/**
 * Vocabulary of the organization settings and the admin wizard (Onboarding Admin 03–11, Einstellungen 20–24,
 * Admin 03–05): legal forms, VAT ID normalization, hostname rules of the portal domain, the contrast sentence of the
 * brand colour, module/step/legal-text labels. The server sends the labels of its rows; these helpers serve inputs that
 * validate before a request (VAT field, domain fields, colour picker). German strings verbatim from the screens where a
 * frame shows them; the others are own and reported in the API doc.
 */
/** „Rechtsform" select of Admin 04 (own list: the German forms a broker or developer company uses). */
export const legalForms = [
    'GmbH',
    'UG (haftungsbeschränkt)',
    'AG',
    'GmbH & Co. KG',
    'KG',
    'OHG',
    'e. K.',
    'GbR',
    'Einzelunternehmen',
];
// ---------------------------------------------------------------------------------------------
// VAT ID (Admin 04, E20)
// ---------------------------------------------------------------------------------------------
/** Removes spaces, dots and hyphens and upper-cases the country prefix: „de 312 448 901" → „DE312448901". */
export function normalizeVatId(input) {
    return input.replace(/[\s.-]/g, '').toUpperCase();
}
/** A German VAT ID after normalization: „DE" + nine digits. */
export const germanVatIdPattern = /^DE\d{9}$/;
export function isWellFormedVatId(normalized) {
    return germanVatIdPattern.test(normalized);
}
/** Display form of E20: „DE 312 448 901"; anything not well-formed is returned as entered. */
export function formatVatId(value) {
    const normalized = normalizeVatId(value);
    if (!isWellFormedVatId(normalized))
        return value;
    return `DE ${normalized.slice(2, 5)} ${normalized.slice(5, 8)} ${normalized.slice(8)}`;
}
/** Field state line of the VAT field (Admin 04 „Umsatzsteuer-ID · nicht bestätigt"); `invalid` is verbatim. */
export const vatCheckLabels = {
    valid: 'Umsatzsteuer-ID bestätigt',
    invalid: 'Das MIAS-Register kennt diese Nummer nicht. Elf Stellen nach DE prüfen.',
    unavailable: 'Das MIAS-Register ist gerade nicht erreichbar. Die USt-ID bleibt unbestätigt, bis Sie erneut prüfen.',
    unchecked: 'Noch nicht geprüft',
};
export const vatIdFormatMessage = 'Die USt-ID besteht aus DE und neun Ziffern, zum Beispiel DE 312 448 901.';
// ---------------------------------------------------------------------------------------------
// Company fields that the frames show as one input each (Admin 04: „PLZ und Ort", „Registergericht und Nummer")
// ---------------------------------------------------------------------------------------------
/** „93047 Regensburg" → `{ postalCode: '93047', city: 'Regensburg' }`; null when there is no five-digit code + city. */
export function splitPostalCodeCity(input) {
    const match = /^\s*(\d{5})\s+(\S.*?)\s*$/.exec(input);
    if (match === null)
        return null;
    return { postalCode: match[1] ?? '', city: match[2] ?? '' };
}
export function joinPostalCodeCity(postalCode, city) {
    return [postalCode, city].filter((part) => part !== null && part !== '').join(' ');
}
/**
 * „AG Regensburg · HRB 18442" → `{ court: 'AG Regensburg', number: 'HRB 18442' }`. The number starts with the register
 * kind (HRA, HRB, GnR, PR, VR) or is the part after „·"/„,"; null when no number can be told apart.
 */
export function splitRegister(input) {
    const text = input.trim().replace(/\s+/g, ' ');
    const kind = /^(.*?)[\s,·|]*\b((?:HRA|HRB|GnR|PR|VR)\s*\d[\w\s]*)$/i.exec(text);
    if (kind !== null) {
        const court = (kind[1] ?? '').replace(/[\s,·|]+$/, '').trim();
        const number = (kind[2] ?? '')
            .trim()
            .replace(/^(HRA|HRB|GnR|PR|VR)\s*/i, (prefix) => `${prefix.trim().toUpperCase()} `);
        if (court !== '' && number !== '')
            return { court, number };
    }
    const separated = /^(.+?)\s*[·,|]\s*(.+)$/.exec(text);
    if (separated !== null) {
        const court = (separated[1] ?? '').trim();
        const number = (separated[2] ?? '').trim();
        if (court !== '' && /\d/.test(number))
            return { court, number };
    }
    return null;
}
export function joinRegister(court, number) {
    return [court, number].filter((part) => part !== null && part !== '').join(' · ');
}
// ---------------------------------------------------------------------------------------------
// Brand (Admin 03, E20)
// ---------------------------------------------------------------------------------------------
/** The four swatches of Admin 03/E20 (tokens accent, navy-900, success-fg, sand-500). */
export const brandColorPresets = ['#E8701F', '#0C1727', '#4A5F3B', '#A9855A'];
export const brandColorPattern = /^#[0-9A-Fa-f]{6}$/;
function channel(value) {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
/** WCAG 2.x contrast ratio of a hex colour against white. */
export function contrastAgainstWhite(hex) {
    const value = hex.replace('#', '');
    const r = channel(Number.parseInt(value.slice(0, 2), 16));
    const g = channel(Number.parseInt(value.slice(2, 4), 16));
    const b = channel(Number.parseInt(value.slice(4, 6), 16));
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return 1.05 / (luminance + 0.05);
}
/**
 * Contrast sentence under the colour (Admin 03): ≥ 3 : 1 the frame's sentence (white text on the colour from 16 px),
 * ≥ 4.5 : 1 and below 3 : 1 own sentences.
 */
export function contrastLabel(hex) {
    if (!brandColorPattern.test(hex))
        return '';
    const ratio = contrastAgainstWhite(hex);
    if (ratio >= 4.5)
        return 'Kontrast gegen Weiß geprüft · ausreichend für Text jeder Größe';
    if (ratio >= 3)
        return 'Kontrast gegen Weiß geprüft · ausreichend für Text ab 16 px';
    return 'Kontrast gegen Weiß zu gering · weiße Schrift auf dieser Farbe ist schwer lesbar';
}
export const brandLogoSlots = ['light', 'dark', 'favicon'];
// ---------------------------------------------------------------------------------------------
// Legal texts (Admin 05, E21)
// ---------------------------------------------------------------------------------------------
export const organizationLegalTextTypes = ['imprint', 'privacy', 'terms', 'withdrawal'];
export const legalTextTitles = {
    imprint: 'Impressum',
    privacy: 'Datenschutzerklärung',
    terms: 'Allgemeine Geschäftsbedingungen',
    withdrawal: 'Widerrufsbelehrung',
};
/**
 * The texts behind `portalPublished` (decision M8: imprint, privacy, withdrawal). Since D-333 (29.10.2026) they no longer
 * gate the portal; the flag is informational.
 */
export const portalRequiredLegalTexts = ['imprint', 'privacy', 'withdrawal'];
export const legalTextMaxChars = 60_000;
// ---------------------------------------------------------------------------------------------
// Portal domain (Admin 06–08, E22–E24)
// ---------------------------------------------------------------------------------------------
/** Hosts under the organization base domain that never become an organization address. */
export const reservedOrganizationHosts = ['kunden', 'www', 'api', 'git', 'mail'];
const labelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
/** Lower-cases, trims and drops a trailing dot and a leading scheme/path the user may paste. */
export function normalizeHostname(input) {
    return input
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/\.$/, '');
}
export function isValidSubdomainLabel(label) {
    return labelPattern.test(label);
}
/** A domain of at least two labels with an alphabetic top-level label („brimo.de", „kunden.brimo.co.uk"). */
export function isValidDomainName(domain) {
    if (domain.length > 253)
        return false;
    const labels = domain.split('.');
    if (labels.length < 2)
        return false;
    if (!labels.every((label) => labelPattern.test(label)))
        return false;
    return /^[a-z]{2,}$/.test(labels[labels.length - 1] ?? '');
}
// ---------------------------------------------------------------------------------------------
// Modules (Admin 05 „Module freischalten") and the admin wizard (Onboarding Admin 03–11)
// ---------------------------------------------------------------------------------------------
export const organizationModuleKeys = [
    'dialer',
    'financing',
    'tickets_after_sales',
    'client_portal',
    'video_consulting',
    'property_acquisition',
    // 0044 (PKV)
    'pkv',
];
export const organizationModuleCards = {
    dialer: {
        name: 'Dialer',
        description: 'Listen, Kampagnen und Anrufsessions für Setter und Berater.',
        note: 'Kosten je Nummer laufen über Telefonie.',
    },
    financing: {
        name: 'Finanzierungen',
        description: 'Pipeline „Finanzierung“, externe Finanzierer, Angebote.',
        note: null,
    },
    tickets_after_sales: {
        name: 'Tickets und After-Sales',
        description: 'Support-Postfach, Ticket-Board, Mängel nach Übergabe.',
        note: null,
    },
    client_portal: {
        name: 'Mandantenportal',
        description: 'Kunden sehen Unterlagen, Termine und Status.',
        note: 'Ausschalten sperrt alle Portal-Zugänge sofort.',
    },
    video_consulting: {
        name: 'Videoberatung',
        description: 'Räume, Aufzeichnung und Transkript.',
        note: 'Einschalten braucht die Einwilligungstexte unter Rechtstexte.',
    },
    property_acquisition: {
        name: 'Objekt-Ankauf',
        description: 'Ankaufsprüfung, Kalkulation, Checkliste vor Freigabe.',
        note: null,
    },
    pkv: {
        name: 'PKV',
        description: 'Risikovoranfragen, Rechner, Anträge und Provisionen für die private Krankenversicherung.',
        note: null,
    },
};
export const organizationSetupStepKeys = [
    'brand',
    'company_data',
    'legal_texts',
    'domain',
    'telephony',
    'team',
    'payment_method',
];
/** The six circles of the wizard stepper (Admin 03–10); `payment_method` appears only on the checklist (Admin 11). */
export const organizationWizardSteps = [
    'brand',
    'company_data',
    'legal_texts',
    'domain',
    'telephony',
    'team',
];
export const organizationSetupStepLabels = {
    brand: 'Marke',
    company_data: 'Firmendaten',
    legal_texts: 'Rechtstexte',
    domain: 'Domain',
    telephony: 'Telefonie',
    team: 'Team',
    payment_method: 'Zahlungsmittel der Firma',
};
//# sourceMappingURL=organization.js.map